import React, { useRef, useEffect, useState, memo } from 'react';
import { Event } from '../types';
import { Calendar, MapPin, Star, Sparkles, EyeOff, X, Hand, Users, Lock, User as UserIcon, Image } from 'lucide-react';
import { getEventImage } from '../services/eventImageService';
import { motion, useInView, useAnimation, PanInfo } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { hideVybe, unhideVybe, hideVybeLocally, unhideVybeLocally } from '../services/hiddenVybesService';
import { getApproximateLocation } from '../utils/locationDisplay';
import { trackVybeCardOpened, trackJoinVybeClicked } from '../services/behaviorTracking';
import toast from 'react-hot-toast';
import LazyImage from './ui/LazyImage';
import TribeMomentumBadge from './TribeMomentumBadge';
import { generatePromoImages } from '../services/promoImageService';

interface EventCardProps {
  event: Event;
  onClick: () => void;
  index?: number;
  isFeatured?: boolean;
  pendingApplicationsCount?: number;
  hideCreator?: boolean;
  onRemoved?: (id: string) => void;
  disableSwipeHint?: boolean;
  enableSwipeHide?: boolean;
  onUndo?: () => void;
  userApplicationStatus?: 'pending' | 'accepted' | 'rejected' | 'waitlisted' | 'withdrawn' | null;
  showCreatedDate?: boolean;
}

const EventCard: React.FC<EventCardProps> = ({
  event,
  onClick,
  index = 0,
  hideCreator = false,
  onRemoved,
  disableSwipeHint = false,
  enableSwipeHide = false,
  onUndo,
  userApplicationStatus = null,
  showCreatedDate = false
}) => {
  const { currentUser, isAuthenticated } = useAuth();
  const poster = event.poster;
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [ghostAnimationCount, setGhostAnimationCount] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [previewedCardsCount, setPreviewedCardsCount] = useState(0);
  const dragStartedRef = useRef(false);
  const [isGeneratingPromo, setIsGeneratingPromo] = useState(false);
  const isAdmin = currentUser?.isAdmin || false;

  useEffect(() => {
    if (isInView) controls.start("visible");
  }, [isInView, controls]);

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Ghost animation disabled - replaced with SwipeEducationModal
  // useEffect(() => {
  //   if (!enableSwipeHide || disableSwipeHint) return;
  //   const hasSeenHint = localStorage.getItem('swipeHintSeen');
  //   const previewedCount = parseInt(localStorage.getItem('previewedCardsCount') || '0', 10);
  //   if (!hasSeenHint && previewedCount < 3 && index < 3 && isInView) {
  //     const timer = setTimeout(() => {
  //       setShowSwipeHint(true);
  //       localStorage.setItem('previewedCardsCount', String(previewedCount + 1));
  //     }, 1200);
  //     return () => clearTimeout(timer);
  //   }
  // }, [index, isInView, disableSwipeHint, enableSwipeHide, prefersReducedMotion]);


  const handleHide = async (direction: 'left' | 'right') => {
    // Mark hint as seen
    localStorage.setItem('swipeHintSeen', '1');
    setShowSwipeHint(false);

    setIsExiting(true);

    // Animate card off screen
    await controls.start({
      x: direction === 'left' ? -1000 : 1000,
      opacity: 0,
      rotate: direction === 'left' ? -15 : 15,
      transition: { duration: 0.5, ease: 'easeInOut' }
    });

    if (onRemoved) {
      onRemoved(event.id);
    }

    if (isAuthenticated && currentUser?.id) {
      const result = await hideVybe(currentUser.id, event.id, `swipe-${direction}`);
      if (!result.success) {
        console.error('Failed to hide vybe');
      }
    } else {
      hideVybeLocally(event.id);
    }

    // Show undo snackbar via toast
    toast(
      (t) => (
        <div
          className="flex items-center gap-3 bg-theme-cards/90 backdrop-blur-md px-4 py-3 rounded-full shadow-lg border border-white/10"
          role="status"
          aria-live="polite"
        >
          <span className="text-white/90 text-sm">Vybe hidden</span>
          <span className="text-white/40">•</span>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              if (isAuthenticated && currentUser?.id) {
                await unhideVybe(currentUser.id, event.id);
              } else {
                unhideVybeLocally(event.id);
              }
              if (onUndo) onUndo();
            }}
            className="text-sm font-medium bg-gradient-to-r from-pink-400 to-cyan-400 bg-clip-text text-transparent hover:from-pink-300 hover:to-cyan-300 transition-all focus:outline-none focus:ring-2 focus:ring-pink-400/50 rounded px-2 py-1"
            aria-label="Undo hide Vybe"
          >
            Undo
          </button>
        </div>
      ),
      {
        duration: 4000,
        position: 'bottom-center',
        style: {
          background: 'transparent',
          boxShadow: 'none',
          padding: 0,
        }
      }
    );
  };

  const handleDragStart = () => {
    dragStartedRef.current = true;
    setIsDragging(true);
    setShowSwipeHint(false);
  };

  const handleDragEnd = (_event: any, info: PanInfo) => {
    const threshold = 120;
    const velocity = Math.abs(info.velocity.x);

    if (Math.abs(info.offset.x) >= threshold || velocity >= 800) {
      const direction = info.offset.x < 0 ? 'left' : 'right';
      handleHide(direction);
    } else {
      controls.start({ x: 0, rotate: 0, scale: 1 });
    }
    setIsDragging(false);
    setDragX(0);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleGeneratePromoImage = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isAdmin) return;

    setIsGeneratingPromo(true);

    try {
      const result = await generatePromoImages(event as any);
      toast.success('Promo images generated and downloaded!', {
        duration: 4000,
        position: 'bottom-center',
      });
      console.log('Generated promo images:', result);
    } catch (error) {
      console.error('Failed to generate promo images:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate promo images';

      if (errorMessage.includes('already in progress')) {
        toast.error('Generation already in progress for this Vybe', {
          duration: 3000,
          position: 'bottom-center',
        });
      } else {
        toast.error(errorMessage, {
          duration: 3000,
          position: 'bottom-center',
        });
      }
    } finally {
      setIsGeneratingPromo(false);
    }
  };

  const calculateDistance = (): number | null => {
    if (
      !currentUser?.latitude ||
      !currentUser?.longitude ||
      !event.latitude ||
      !event.longitude
    ) {
      return null;
    }
    const R = 3958.8; // miles
    const dLat = (event.latitude - currentUser.latitude) * Math.PI / 180;
    const dLon = (event.longitude - currentUser.longitude) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(currentUser.latitude * Math.PI / 180) *
        Math.cos(event.latitude * Math.PI / 180) *
        Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const distance = calculateDistance();

  const dragDirection = dragX < 0 ? 'left' : 'right';
  const dragProgress = Math.min(Math.abs(dragX) / 120, 1);
  const glowColor = dragDirection === 'left'
    ? `rgba(236, 72, 153, ${dragProgress * 0.4})`
    : `rgba(0, 209, 255, ${dragProgress * 0.4})`;

  const getCategoryIcon = (category: string) => {
    const categoryMap: Record<string, string> = {
      'Outdoors & Adventures': '🌲',
      'Fitness & Wellness': '💪',
      'Art & Creative Stuff': '🎨',
      'Shows, Movies & Fun': '🎭',
      'Music & Festivals': '🎶',
      'Gaming & Board Games': '🎮',
      'Sports & Events': '🏟️',
      'Learn Something New': '🧠',
      'Meetups & Socials': '🗣️',
      'Volunteering & Good Vybes': '🤝',
      'Fashion & Style': '👗',
      'Tech & Gadgets': '💻',
      'Books & Chill': '📚',
      'Pets & Animals': '🐶',
      'Dance & Movement': '💃',
      'Travel & Trips': '✈️'
    };

    // Remove emoji if it's already in the string and normalize
    const normalized = category.replace(/[\u{1F300}-\u{1FAFF}]/gu, '').trim();
    const icon = categoryMap[normalized];
    return icon ? `${icon} ${normalized}` : category;
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={showSwipeHint && enableSwipeHide && !isDragging && !dragStartedRef.current && !prefersReducedMotion ? "preview" : controls}
      drag={enableSwipeHide && !isExiting ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.2}
      onPointerDown={() => {
        if (showSwipeHint) {
          dragStartedRef.current = true;
          setShowSwipeHint(false);
        }
      }}
      onDragStart={handleDragStart}
      onDrag={(_event, info) => setDragX(info.offset.x)}
      onDragEnd={handleDragEnd}
      style={{
        scale: isDragging ? 0.98 : 1,
        rotate: isDragging ? dragX * 0.05 : 0,
        boxShadow: isDragging ? `0 0 20px ${glowColor}` : undefined,
        willChange: showSwipeHint || isDragging ? 'transform, box-shadow' : 'auto'
      }}
      variants={{
        hidden: { opacity: 0, y: 20 },
        visible: {
          opacity: 1,
          y: 0,
          transition: {
            duration: 0.3,
            delay: index * 0.05
          }
        },
        preview: {
          opacity: 1,
          y: 0,
          x: ['0%', '20%', '20%', '20%', '0%', '-20%', '-20%', '-20%', '0%'],
          rotate: [0, 4, 4, 4, 0, -4, -4, -4, 0],
          scale: [1, 0.985, 0.985, 0.985, 1, 0.985, 0.985, 0.985, 1],
          boxShadow: [
            'var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow)',
            '0 0 24px rgba(34, 211, 238, 0.4)',
            '0 0 24px rgba(34, 211, 238, 0.4)',
            '0 0 24px rgba(34, 211, 238, 0.4)',
            'var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow)',
            '0 0 24px rgba(236, 72, 153, 0.4)',
            '0 0 24px rgba(236, 72, 153, 0.4)',
            '0 0 24px rgba(236, 72, 153, 0.4)',
            'var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow)'
          ],
          transition: {
            duration: 3.1,
            times: [0, 0.226, 0.339, 0.452, 0.645, 0.742, 0.855, 0.968, 1],
            ease: [[0.25, 0.1, 0.25, 1], [0.42, 0, 0.58, 1], 'linear', [0.42, 0, 0.58, 1], [0.42, 0, 0.58, 1], [0.25, 0.1, 0.25, 1], [0.42, 0, 0.58, 1], 'linear', [0.42, 0, 0.58, 1]],
            repeat: 0,
          },
        }
      }}
      onAnimationComplete={(definition) => {
        if (definition === 'preview' && !dragStartedRef.current) {
          setShowSwipeHint(false);
        }
      }}
      onClick={!isDragging ? () => {
        trackVybeCardOpened(event.id, event.title, event.category, 'feed');
        onClick();
      } : undefined}
      className={`relative w-full bg-theme-cards rounded-2xl overflow-hidden cursor-pointer shadow-card transition-all select-none ${event.isFeatured ? 'ring-2 ring-yellow-400 shadow-yellow-400/50' : ''}`}
    >
      {/* Visual cues that move with card during preview */}
      {showSwipeHint && enableSwipeHide && !isDragging && !dragStartedRef.current && !prefersReducedMotion && (
        <>
          {/* Animated hand glyph (right swipe) */}
          <motion.div
            className="absolute top-1/2 right-8 pointer-events-none z-30 blur-sm"
            initial={{ opacity: 0, x: 0 }}
            animate={{
              opacity: [0, 0.8, 0.8, 0.8, 0],
              x: ['0%', '-200%', '-200%', '-200%', '0%'],
            }}
            transition={{
              duration: 1.55,
              times: [0, 0.452, 0.677, 0.903, 1],
              ease: [[0.25, 0.1, 0.25, 1], 'linear', 'linear', [0.42, 0, 0.58, 1]],
            }}
            style={{ transform: 'translateY(-50%)' }}
          >
            <Hand size={32} className="text-white" style={{ transform: 'rotate(-10deg)' }} />
          </motion.div>

          {/* Animated hand glyph (left swipe) */}
          <motion.div
            className="absolute top-1/2 left-8 pointer-events-none z-30 blur-sm"
            initial={{ opacity: 0, x: 0 }}
            animate={{
              opacity: [0, 0, 0, 0, 0, 0.8, 0.8, 0.8, 0],
              x: ['0%', '0%', '0%', '0%', '0%', '200%', '200%', '200%', '0%'],
            }}
            transition={{
              duration: 3.1,
              times: [0, 0.226, 0.339, 0.452, 0.645, 0.742, 0.855, 0.968, 1],
              ease: ['linear', 'linear', 'linear', 'linear', [0.25, 0.1, 0.25, 1], 'linear', 'linear', [0.42, 0, 0.58, 1]],
            }}
            style={{ transform: 'translateY(-50%)' }}
          >
            <Hand size={32} className="text-white" style={{ transform: 'rotate(10deg) scaleX(-1)' }} />
          </motion.div>

          {/* Edge chevrons (right swipe) */}
          <motion.div
            className="absolute top-1/2 right-4 pointer-events-none z-25"
            initial={{ opacity: 0, x: 0 }}
            animate={{
              opacity: [0, 0.3, 0.3, 0.3, 0],
              x: ['0%', '-100%', '-100%', '-100%', '0%'],
            }}
            transition={{
              duration: 1.55,
              times: [0, 0.452, 0.677, 0.903, 1],
              ease: [[0.25, 0.1, 0.25, 1], 'linear', 'linear', [0.42, 0, 0.58, 1]],
            }}
            style={{ transform: 'translateY(-50%)' }}
          >
            <div className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-magenta-400 bg-clip-text text-transparent">
              ›››
            </div>
          </motion.div>

          {/* Edge chevrons (left swipe) */}
          <motion.div
            className="absolute top-1/2 left-4 pointer-events-none z-25"
            initial={{ opacity: 0, x: 0 }}
            animate={{
              opacity: [0, 0, 0, 0, 0, 0.3, 0.3, 0.3, 0],
              x: ['0%', '0%', '0%', '0%', '0%', '100%', '100%', '100%', '0%'],
            }}
            transition={{
              duration: 3.1,
              times: [0, 0.226, 0.339, 0.452, 0.645, 0.742, 0.855, 0.968, 1],
              ease: ['linear', 'linear', 'linear', 'linear', [0.25, 0.1, 0.25, 1], 'linear', 'linear', [0.42, 0, 0.58, 1]],
            }}
            style={{ transform: 'translateY(-50%) scaleX(-1)' }}
          >
            <div className="text-2xl font-bold bg-gradient-to-r from-magenta-400 to-cyan-400 bg-clip-text text-transparent">
              ›››
            </div>
          </motion.div>

          {/* Colored rim glow (right edge - cyan) */}
          <motion.div
            className="absolute top-0 right-0 bottom-0 w-1 pointer-events-none z-21"
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0, 0, 0.6, 0.6, 0],
            }}
            transition={{
              duration: 1.55,
              times: [0, 0.226, 0.452, 0.677, 1],
              ease: 'easeInOut',
            }}
            style={{
              background: 'linear-gradient(to bottom, rgba(34, 211, 238, 0.8), rgba(34, 211, 238, 0.4))',
              boxShadow: '0 0 20px rgba(34, 211, 238, 0.6)',
            }}
          />

          {/* Colored rim glow (left edge - magenta) */}
          <motion.div
            className="absolute top-0 left-0 bottom-0 w-1 pointer-events-none z-21"
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0, 0, 0, 0, 0, 0, 0.6, 0.6, 0],
            }}
            transition={{
              duration: 3.1,
              times: [0, 0.226, 0.339, 0.452, 0.645, 0.742, 0.855, 0.968, 1],
              ease: 'easeInOut',
            }}
            style={{
              background: 'linear-gradient(to bottom, rgba(236, 72, 153, 0.8), rgba(236, 72, 153, 0.4))',
              boxShadow: '0 0 20px rgba(236, 72, 153, 0.6)',
            }}
          />
        </>
      )}

      {/* Static chevrons for reduced-motion users */}
      {prefersReducedMotion && showSwipeHint && enableSwipeHide && !isDragging && !dragStartedRef.current && (
        <>
          <div className="absolute top-1/2 right-4 pointer-events-none z-25" style={{ transform: 'translateY(-50%)' }}>
            <div className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-magenta-400 bg-clip-text text-transparent opacity-30">
              ›››
            </div>
          </div>
          <div className="absolute top-1/2 left-4 pointer-events-none z-25" style={{ transform: 'translateY(-50%) scaleX(-1)' }}>
            <div className="text-2xl font-bold bg-gradient-to-r from-magenta-400 to-cyan-400 bg-clip-text text-transparent opacity-30">
              ›››
            </div>
          </div>
        </>
      )}

      {/* Background image */}
      <div className="relative aspect-[4/3]">
        {event.imageGenerationStatus === 'generating' || event.imageGenerationStatus === 'pending' ? (
          <div className="w-full h-full bg-gradient-to-br from-pink-500/20 to-cyan-500/20 backdrop-blur-sm flex items-center justify-center">
            <div className="text-center">
              <div className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-pink-500 to-cyan-500 rounded-full shadow-lg">
                <span className="text-sm font-semibold text-white">Image on its way!</span>
              </div>
            </div>
          </div>
        ) : (
          <>
            <LazyImage
              src={getEventImage(event)}
              alt={event.title}
              className="w-full h-full"
              aspectRatio="4/3"
              objectFit="cover"
            />
            {event.unsplashPhotographerName && event.unsplashPhotographerUsername && (
              <div className="absolute top-2 left-2 z-15">
                <a
                  href={`https://unsplash.com/@${event.unsplashPhotographerUsername}?utm_source=vybe_tribe&utm_medium=referral`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-[10px] text-white/80 bg-black/40 backdrop-blur-sm px-2 py-1 rounded hover:bg-black/60 transition-colors inline-block"
                >
                  Photo by {event.unsplashPhotographerName}
                </a>
              </div>
            )}
          </>
        )}
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/80 via-black/30 to-transparent z-10"></div>

        {/* Hide button (desktop) + Featured icon + Type badge + Tribe Momentum badge + Visibility badge */}
        <div className="absolute top-2 right-2 z-20 flex gap-2">
          {/* Show "Applied" badge if user has applied (but not if withdrawn) */}
          {userApplicationStatus && userApplicationStatus !== 'withdrawn' ? (
            <div className="bg-black/50 border border-cyan-400 text-cyan-400 px-2 py-0.5 rounded-full text-xs flex items-center font-medium backdrop-blur-sm">
              <Hand size={12} className="mr-1" /> Applied
            </div>
          ) : null}
          {/* Community/Hosted Vybe badge temporarily hidden for testing */}
          {/* {(event.isBotCreated || event.systemHost) ? (
            <div className="bg-pink-500 text-white px-2 py-0.5 rounded-full text-xs flex items-center font-semibold">
              <Sparkles size={12} className="mr-1" /> Community Vybe
            </div>
          ) : (
            <div className="bg-purple-500 text-white px-2 py-0.5 rounded-full text-xs flex items-center font-semibold">
              <UserIcon size={12} className="mr-1" /> Hosted Vybe
            </div>
          )} */}
          {/* Tribe Momentum badge - shows social proof */}
          {/* Temporarily hidden for testing */}
          {/* {event.acceptedCount !== undefined && (
            <TribeMomentumBadge acceptedCount={event.acceptedCount} />
          )} */}
          {event.featured && (
            <div className="bg-black/50 border border-yellow-400 text-yellow-400 px-2 py-0.5 rounded-full text-xs flex items-center font-medium backdrop-blur-sm">
              <Sparkles size={12} className="mr-1" /> Featured
            </div>
          )}
          {event.visibility === 'tribe_only' && (
            <div className="bg-black/50 border border-blue-400 text-blue-400 px-2 py-0.5 rounded-full text-xs flex items-center font-medium backdrop-blur-sm">
              <Users size={12} className="mr-1" /> Tribe Only
            </div>
          )}
          {isAdmin && (
            <motion.button
              onClick={handleGeneratePromoImage}
              disabled={isGeneratingPromo}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center justify-center px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg border border-white/20 backdrop-blur-sm"
              aria-label="Generate promo image"
              title="Generate promo image for social sharing"
            >
              {isGeneratingPromo ? (
                <span className="text-white text-xs font-semibold">Generating...</span>
              ) : (
                <>
                  <Image size={12} className="text-white mr-1" />
                  <span className="text-white text-xs font-semibold">Generate</span>
                </>
              )}
            </motion.button>
          )}
          {enableSwipeHide && (
            <motion.button
              onClick={(e) => {
                e.stopPropagation();
                handleHide('left');
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="hidden md:flex items-center justify-center w-9 h-9 rounded-full bg-black/70 backdrop-blur-sm hover:bg-black/90 transition-all shadow-lg border border-white/10"
              aria-label="Hide this Vybe"
              title="Hide this Vybe"
            >
              <EyeOff size={16} className="text-white" />
            </motion.button>
          )}
        </div>

        {/* Overlay content bottom */}
        <div className="absolute bottom-0 left-0 right-0 z-20 text-white p-3 bg-gradient-to-t from-black/90 to-transparent">
          <h3 className="font-bold text-base sm:text-lg leading-tight line-clamp-1 mb-1">{event.title}</h3>
          <div className="space-y-0.5 text-xs text-gray-200">
            <div className="flex items-center gap-1">
              <Calendar size={12} className="text-theme-primary flex-shrink-0" />
              <span className="truncate">{formatDate(event.date)}</span>
            </div>
            <div className="flex items-center gap-1">
              <MapPin size={12} className="text-theme-primary flex-shrink-0" />
              <span className="truncate">{getApproximateLocation(event.location)}</span>
              {distance !== null && distance < 50 && (
                <span className="text-theme-text-secondary ml-1">• {distance.toFixed(1)}mi</span>
              )}
            </div>
            {showCreatedDate && (
              <div className="text-[10px] text-gray-400 mt-1">
                Created {formatDate(event.createdAt)}
              </div>
            )}
          </div>
          <div className="mt-2">
            <span className="inline-block px-2 py-1 rounded-full text-xs bg-primary-100 text-contrast font-medium">
              {getCategoryIcon(event.category)}
            </span>
          </div>
        </div>
      </div>

      {/* Logged-out CTA inside card */}
      {!isAuthenticated && (
        <div className="p-3 space-y-1.5">
          <p className="text-xs text-center text-white/60 mb-1">
            Join instantly — leave anytime.
          </p>
          <button
            onClick={(e) => {
              e.stopPropagation();
              trackJoinVybeClicked(event.id, event.title, 'card_cta', event.isBotCreated || event.systemHost || false);
              onClick();
            }}
            className="w-full bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white font-bold py-2.5 px-4 rounded-lg transition-all shadow-lg hover:shadow-xl min-h-[44px] text-sm"
          >
            Join this Vybe (free)
          </button>
          <p className="text-xs text-center text-theme-text-secondary">
            No payment. Not dating. Join or leave freely.
          </p>
        </div>
      )}
    </motion.div>
  );
};

// Custom comparison function to prevent unnecessary re-renders
const arePropsEqual = (prevProps: EventCardProps, nextProps: EventCardProps) => {
  // Only re-render if the event ID, application status, or key event properties change
  return (
    prevProps.event.id === nextProps.event.id &&
    prevProps.userApplicationStatus === nextProps.userApplicationStatus &&
    prevProps.event.applications_count === nextProps.event.applications_count &&
    prevProps.enableSwipeHide === nextProps.enableSwipeHide &&
    prevProps.isFeatured === nextProps.isFeatured
  );
};

export default React.memo(EventCard, arePropsEqual);
